import React, { useState } from 'react'
import {
    Form,
    Card,
    CardWithBreadcrumb,
    FormikSimpleSelect,
    FormikInput,
    Divider,
    Row,
    Col,
    Button,
    FormikCheckboxArray,
    FormikNumberMaskedInput,
    FormikDate
} from '../../Elements'

import { CanEditInventory } from '../Auth'
import { CAN_EDIT_INVENTORY } from '../../utils/enums'
import { useHasRight } from '../../Hooks/hasRight'

import { FieldArray } from 'formik'
import { Intent, NonIdealState } from '@blueprintjs/core'

import { AutoSizer, Table as VirtualTable, Column } from 'react-virtualized'
import './styles.css';
import _ from 'lodash'

const cardStyle = {
    minWidth: 300
}

const evenRow = {
    backgroundColor: 'rgba(243, 239, 253, 0.692)'
}

const calculateHeightStyle = () => {
    return {
        height: window.innerHeight * .65
    }
}

const SearchResults = ({ filteredItems, touched }) => {
    const canEditValues = useHasRight(CAN_EDIT_INVENTORY)

    const headerRenderer = ({ label }) => <p>{label}</p>

    return <div style={{ height: '100%' }}>
        <AutoSizer>
            {({ height, width }) => {
                return <VirtualTable
                    width={width}
                    height={height}
                    headerHeight={30}
                    overscanRowCount={5}
                    rowCount={filteredItems.length}
                    rowHeight={60}
                    rowStyle={(r) => r.index % 2 === 0 ? evenRow : {}}
                    rowGetter={({ index }) => filteredItems[index]}
                >
                    <Column
                        label='Name'
                        dataKey='name'
                        flexShrink={1}
                        width={width * .25}
                        cellDataGetter={({ rowData }) => rowData}
                        cellRenderer={({ cellData }) => <span>
                            {cellData.product.name}
                        </span>
                        }
                        headerRenderer={headerRenderer}
                    />
                    <Column
                        label='Description'
                        dataKey='description'
                        flexShrink={1}
                        width={width * .15}
                        cellDataGetter={({ rowData }) => rowData}
                        cellRenderer={({ cellData }) => <span>
                            {cellData.product.description}
                        </span>
                        }
                        headerRenderer={headerRenderer} />
                    <Column
                        label='Units'
                        dataKey='unit_count'
                        flexShrink={1}
                        width={width * .15}
                        cellDataGetter={({ rowData }) => rowData}
                        cellRenderer={({ cellData }) => canEditValues
                            ? <FormikNumberMaskedInput
                                key={`inventory-${cellData.valuesindex}-unit_count`}
                                name={`inventory.${cellData.valuesIndex}.unit_count`}
                                groupStyles={{ margin: '.2em' }}
                                type='number'
                            />
                            : <span>{cellData.unit_count}</span>
                        }
                        headerRenderer={headerRenderer} />
                    <Column
                        label='Individual'
                        dataKey='individual_count'
                        flexShrink={1}
                        width={width * .15}
                        cellDataGetter={({ rowData }) => rowData}
                        cellRenderer={({ cellData }) => canEditValues
                            ? <FormikNumberMaskedInput
                                key={`inventory-${cellData.valuesIndex}-individual_count`}
                                name={`inventory.${cellData.valuesIndex}.individual_count`}
                                groupStyles={{ margin: '.2em' }}
                                type='number'
                            />
                            : <span>{cellData.individual_count}</span>
                        }
                        headerRenderer={headerRenderer} />
                    <Column
                        label='Last Count Date'
                        dataKey='count_date'
                        flexShrink={1}
                        width={width * .15}
                        cellDataGetter={({ rowData }) => rowData}
                        cellRenderer={({ cellData }) => canEditValues
                            ? <FormikDate
                                groupStyles={{ margin: '.2em' }}
                                tabIndex={'-1'}
                                key={`inventory-${cellData.valuesIndex}-count_date`}
                                name={`inventory.${cellData.valuesIndex}.count_date`}
                                intent={touched.inventory && touched.inventory[cellData.valuesIndex] && touched.inventory[cellData.valuesIndex].count_date ? Intent.WARNING : null}
                            />
                            : <span>{cellData.count_date}</span>
                        }
                        headerRenderer={headerRenderer} />
                    <Column
                        label='Location'
                        dataKey='location'
                        flexShrink={1}
                        maxWidth={width * .15}
                        cellDataGetter={({ rowData }) => rowData}
                        cellRenderer={({ cellData }) => <span>
                            {cellData.location.name}
                        </span>
                        }
                    />
                </VirtualTable>
            }}
        </AutoSizer>
    </div>
}


export default ({ values = {}, dirty, lookupOptions = {}, touched, employees, ...props }) => {
    const [itemsToRender, setItemsToRender] = useState([])

    const [locationValue, setLocationValue] = useState(values.location)
    const [categoriesValue, setCategoriesValue] = useState(values.product_categories)
    const filterItems = () => {
        const filtered = values.inventory.filter(entry => {
            const entryMatchesSelectedLocation = values.location && entry.location && entry.location.id === values.location.id
            const entryMatchesSelectedTypes =
                entry?.product?.category?.id && values.product_categories.some(c => c.id === entry.product.category.id)
            const entryMatchesAsUncategorized = values.product_categories.some(t => t.id === -1)
                ? !entry?.product?.category?.id
                : false
            return (entryMatchesSelectedLocation && (entryMatchesSelectedTypes || entryMatchesAsUncategorized))
        })

        setItemsToRender(filtered)
    }

    if (values.location !== locationValue || !_.isEqual(_.sortBy(values.product_categories), _.sortBy(categoriesValue))) {
        setLocationValue(values.location)
        setCategoriesValue(values.product_categories)
        filterItems()
    }

    return (
        <Form>
            <CardWithBreadcrumb items={[{ text: 'Inventory', icon: 'numerical' }, { text: values.location && values.location.name }]}>
                <CanEditInventory>
                    <Button intent={Intent.SUCCESS} text='Update Inventory' type='submit' />
                </CanEditInventory>
            </CardWithBreadcrumb>

            <Card style={cardStyle}>
                <Row>
                    <Col lg={1}>
                        <Row>
                            <FormikInput
                                label='Update Date'
                                name='update_date'
                                disabled
                            />
                        </Row>
                    </Col>
                    <Col lg={10.99}>
                        <Col lg={3}>
                            <FormikSimpleSelect
                                label='Location'
                                name='location'
                                items={lookupOptions.lookupInventoryLocation}
                                nullable
                            />
                        </Col>
                        <Divider />
                    </Col>
                </Row>
                <Row>
                    <Col lg={1}>
                        <FormikCheckboxArray
                            label='Product Categories'
                            name='product_categories'
                            value={values.product_categories}
                            items={lookupOptions.lookupProductCategory}
                        />
                    </Col>
                    <Divider />
                    <Col lg={10.99} style={calculateHeightStyle()}>
                        {itemsToRender.length > 0 &&
                            <FieldArray
                                name='inventory'
                                render={() => (
                                    <span>
                                        {values.location && <SearchResults
                                            filteredItems={itemsToRender}
                                            touched={touched}
                                        />
                                        }
                                    </span>
                                )} />
                        }
                        {
                            itemsToRender.length < 1 &&
                            <NonIdealState
                                icon={values.location ? 'filter' : 'geosearch'}
                                title={values.location ? 'No results for that combination of filters.' : 'Select a location to get started!'}
                            />
                        }
                    </Col>
                </Row>
            </Card>
        </Form >
    )
}